import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, Upload, File, FileText, Film, Music, Archive, Download, Trash2, Eye } from 'lucide-react'
import { getTodoFiles, uploadTodoFile, deleteTodoFile, getFileUrl, downloadFile, type TodoFile } from '../lib/fileStorage'
import { getAcceptAttribute } from '../lib/fileValidation'
import { Capacitor } from '@capacitor/core'
import { isNativeApp } from '../lib/platform'
import { FilePicker } from '@capawesome/capacitor-file-picker'
import PdfViewer from './PdfViewer'
import ConfirmDialog from './ConfirmDialog'

interface TodoFileModalProps {
  todoId: string
  todoTitle: string
  isOpen: boolean
  onClose: () => void
}

interface FileWithUrl extends TodoFile {
  downloadUrl?: string
}

const getCategoryIcon = (category: TodoFile['file_category']) => {
  switch (category) {
    case 'document':
      return <FileText className="h-5 w-5 text-blue-500" />
    case 'video':
      return <Film className="h-5 w-5 text-purple-500" />
    case 'audio':
      return <Music className="h-5 w-5 text-green-500" />
    case 'archive':
      return <Archive className="h-5 w-5 text-orange-500" />
    default:
      return <File className="h-5 w-5 text-gray-500" />
  }
}

const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return '不明'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function TodoFileModal({ todoId, todoTitle, isOpen, onClose }: TodoFileModalProps) {
  const [files, setFiles] = useState<FileWithUrl[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [previewFile, setPreviewFile] = useState<TodoFile | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string>('')
  const [windowSize, setWindowSize] = useState({ width: 1200, height: 800 })
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false)
  const [pdfViewerUrl, setPdfViewerUrl] = useState('')
  const [pdfViewerFileName, setPdfViewerFileName] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      loadFiles()
    }
  }, [isOpen, todoId])

  // ウィンドウサイズの変更を監視
  useEffect(() => {
    const updateWindowSize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      })
    }

    // 初期値を設定
    updateWindowSize()

    // リサイズイベントをリッスン
    window.addEventListener('resize', updateWindowSize)

    return () => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [])

  const loadFiles = async () => {
    setLoading(true)
    setError(null)
    const result = await getTodoFiles(todoId)
    if (result.success && result.files) {
      setFiles(result.files)
    } else {
      setError(result.error || 'ファイルの読み込みに失敗しました')
    }
    setLoading(false)
  }

  // ネイティブファイル選択（モバイルアプリ用）
  const handleNativeFilePick = async () => {
    try {
      setUploading(true)
      setError(null)

      const result = await FilePicker.pickFiles({
        types: [
          'application/pdf',
          'video/*',
          'audio/*',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          'application/zip',
          'application/x-rar-compressed',
          'application/x-7z-compressed',
          'text/plain',
          'text/csv'
        ],
        readData: true
      })

      if (result.files && result.files.length > 0) {
        const pickedFile = result.files[0]

        // Base64データをBlobに変換
        if (!pickedFile.data) {
          throw new Error('ファイルデータの読み込みに失敗しました')
        }

        const byteString = atob(pickedFile.data)
        const arrayBuffer = new ArrayBuffer(byteString.length)
        const uint8Array = new Uint8Array(arrayBuffer)
        for (let i = 0; i < byteString.length; i++) {
          uint8Array[i] = byteString.charCodeAt(i)
        }

        const blob = new Blob([uint8Array], { type: pickedFile.mimeType || 'application/octet-stream' })

        // BlobをFileオブジェクトとして扱う
        const file = Object.assign(blob, {
          name: pickedFile.name || 'file',
          lastModified: Date.now()
        }) as File

        const uploadResult = await uploadTodoFile(todoId, file)

        if (uploadResult.success) {
          await loadFiles()
        } else {
          setError(uploadResult.error || 'アップロードに失敗しました')
        }
      }
    } catch (err: any) {
      console.error('Native file pick error:', err)
      if (err.message !== 'pickFiles canceled') {
        setError(err.message || 'ファイルの選択に失敗しました')
      }
    } finally {
      setUploading(false)
    }
  }

  // Web版ファイル選択（ブラウザ用）
  const handleWebFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setUploading(true)
    setError(null)

    const result = await uploadTodoFile(todoId, file)

    if (result.success) {
      await loadFiles()
    } else {
      setError(result.error || 'アップロードに失敗しました')
    }

    setUploading(false)
    event.target.value = ''
  }

  const handleFileSelect = async (event?: React.ChangeEvent<HTMLInputElement>) => {
    const isNative = Capacitor.isNativePlatform()

    if (isNative) {
      // ネイティブアプリ: FilePicker API
      await handleNativeFilePick()
    } else {
      // Web版: HTMLのinput要素
      if (event) {
        await handleWebFileSelect(event)
      }
    }
  }

  const handleDelete = (fileId: string) => {
    setPendingDeleteId(fileId)
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = async () => {
    if (!pendingDeleteId) return
    setShowDeleteConfirm(false)

    const result = await deleteTodoFile(pendingDeleteId)

    if (result.success) {
      await loadFiles()
    } else {
      setError(result.error || '削除に失敗しました')
    }

    setPendingDeleteId(null)
  }

  const handleDownload = async (file: TodoFile) => {
    try {
      await downloadFile(file.file_url, file.file_name)
    } catch (error) {
      setError('ダウンロードに失敗しました')
    }
  }

  const handlePreview = async (file: TodoFile) => {
    try {
      const url = await getFileUrl(file.file_url)

      // PDF の場合は専用ビューアを使用（モバイル・デスクトップ両対応）
      if (file.file_type === 'application/pdf') {
        setPdfViewerUrl(url)
        setPdfViewerFileName(file.file_name)
        setPdfViewerOpen(true)
      } else {
        setPreviewUrl(url)
        setPreviewFile(file)
      }
    } catch (error) {
      setError('プレビューの読み込みに失敗しました')
    }
  }

  const handlePdfDownload = async () => {
    const pdfFile = files.find(f => f.file_name === pdfViewerFileName)
    if (pdfFile) {
      await handleDownload(pdfFile)
    }
  }

  /**
   * モバイルデバイス判定（UserAgent）
   * ネイティブアプリだけでなく、モバイルブラウザも検出
   */
  const isMobileDevice = (): boolean => {
    if (isNativeApp()) return true
    if (typeof navigator === 'undefined') return false
    return /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  }

  /**
   * プラットフォーム別にプレビュー可能なファイルタイプを判定
   * - デスクトップWeb: PDF、画像、テキスト、Office文書
   * - モバイル（ネイティブ/ブラウザ）: PDF、画像のみ
   */
  const canPreview = (file: TodoFile): boolean => {
    const fileType = file.file_type || ''

    // 画像は全プラットフォームでプレビュー可能
    if (fileType.startsWith('image/')) return true

    // PDFは全プラットフォームでプレビュー可能
    if (fileType === 'application/pdf') return true

    // モバイルデバイスの場合、上記以外はプレビュー不可
    if (isMobileDevice()) return false

    // デスクトップWebのみ: テキスト系ファイル
    if (fileType === 'text/plain' || fileType === 'text/csv') return true

    // デスクトップWebのみ: Office文書
    const officeTypes = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ]
    if (officeTypes.includes(fileType)) return true

    return false
  }

  const isOfficeDocument = (fileType: string | null): boolean => {
    const officeTypes = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ]
    return officeTypes.includes(fileType || '')
  }

  if (!isOpen) return null

  // Preview mode
  if (previewFile && previewUrl) {
    // ウィンドウサイズを取得して、ほぼフルサイズ（95%）で表示
    const modalWidth = Math.min(windowSize.width * 0.95, 1600) // 95%のサイズ、最大1600px
    const modalHeight = Math.min(windowSize.height * 0.95, 1000) // 95%のサイズ、最大1000px

    return createPortal(
      <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-2 sm:p-4 animate-fade-slide-in">
        <button
          onClick={() => {
            setPreviewFile(null)
            setPreviewUrl('')
          }}
          className="absolute top-2 right-2 sm:top-4 sm:right-4 p-2 text-white hover:bg-white/20 rounded-full z-10 tap-highlight transition-colors"
          aria-label="閉じる"
        >
          <X className="h-5 w-5 sm:h-6 sm:w-6" />
        </button>

        <div
          className="bg-white rounded-xl overflow-hidden flex flex-col shadow-2xl animate-scale-in"
          style={{
            width: windowSize.width < 768 ? '95vw' : `${modalWidth}px`,
            height: windowSize.width < 768 ? '95vh' : `${modalHeight}px`,
            maxWidth: '95vw',
            maxHeight: '95vh'
          }}
        >
          <div className="flex items-center justify-between p-3 sm:p-4 border-b bg-gray-50 gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <div className="flex-shrink-0">
                {getCategoryIcon(previewFile.file_category)}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{previewFile.file_name}</h3>
                <p className="text-xs text-gray-500">{formatFileSize(previewFile.file_size)}</p>
              </div>
            </div>
            <button
              onClick={() => handleDownload(previewFile)}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex-shrink-0 text-xs sm:text-sm tap-highlight transition-colors"
            >
              <Download className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">ダウンロード</span>
            </button>
          </div>

          <div className="flex-1 overflow-auto bg-gray-100">
            {previewFile.file_type === 'application/pdf' ? (
              <iframe
                src={previewUrl}
                className="w-full h-full"
                style={{ minHeight: '500px' }}
                title={previewFile.file_name}
              />
            ) : isOfficeDocument(previewFile.file_type) ? (
              <iframe
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(previewUrl)}`}
                className="w-full h-full"
                style={{ minHeight: '500px' }}
                title={previewFile.file_name}
              />
            ) : previewFile.file_type?.startsWith('image/') ? (
              <div className="flex items-center justify-center p-4">
                <img
                  src={previewUrl}
                  alt={previewFile.file_name}
                  className="max-w-full max-h-[calc(90vh-150px)] object-contain"
                />
              </div>
            ) : previewFile.file_type === 'text/plain' || previewFile.file_type === 'text/csv' ? (
              <iframe
                src={previewUrl}
                className="w-full h-full min-h-[600px] bg-white"
                title={previewFile.file_name}
              />
            ) : (
              <div className="flex items-center justify-center h-full p-8">
                <div className="text-center">
                  <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">このファイル形式はプレビューできません</p>
                  <button
                    onClick={() => handleDownload(previewFile)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    ダウンロードして開く
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body
    )
  }

  const modalContent = (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-2 sm:p-4 animate-fade-slide-in">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b gap-2">
          <h2 className="text-base sm:text-lg font-semibold truncate min-w-0 flex-1">{todoTitle} - ファイル</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0 tap-highlight transition-colors"
            aria-label="閉じる"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-3 sm:mx-4 mt-3 sm:mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Upload button */}
        <div className="p-3 sm:p-4 border-b bg-gray-50">
          {Capacitor.isNativePlatform() ? (
            // ネイティブアプリ: ボタンをクリックしてFilePicker APIを呼び出し
            <button
              onClick={() => handleFileSelect()}
              disabled={uploading || files.length >= 10}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-xs sm:text-sm w-full tap-highlight transition-colors"
            >
              <Upload className="h-4 w-4 flex-shrink-0" />
              <span>ファイルを選択</span>
            </button>
          ) : (
            // Web版: HTMLのlabel+input要素
            <label className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 cursor-pointer text-xs sm:text-sm w-full tap-highlight transition-colors">
              <Upload className="h-4 w-4 flex-shrink-0" />
              <span>ファイルを選択</span>
              <input
                type="file"
                accept={getAcceptAttribute()}
                onChange={handleFileSelect}
                disabled={uploading || files.length >= 10}
                className="hidden"
              />
            </label>
          )}
          {files.length >= 10 && (
            <p className="text-xs text-gray-500 mt-2">最大10ファイルまで添付できます</p>
          )}
          {uploading && (
            <p className="text-xs text-indigo-600 mt-2">アップロード中...</p>
          )}
          <p className="text-xs text-gray-500 mt-2">
            対応形式: PDF, 動画, 音声, ドキュメント, アーカイブなど（最大50MB）
          </p>
        </div>

        {/* File list */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500 text-sm">読み込み中...</div>
          ) : files.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <File className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">ファイルがありません</p>
              <p className="text-xs sm:text-sm mt-1">上のボタンからファイルを追加できます</p>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3">
              {files.map((file) => (
                <div
                  key={file.id}
                  className="flex items-start sm:items-center gap-2 sm:gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 group"
                >
                  {/* Icon */}
                  <div className="flex-shrink-0 mt-0.5 sm:mt-0">
                    {getCategoryIcon(file.file_category)}
                  </div>

                  {/* File info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-xs sm:text-sm break-words">{file.file_name}</p>
                    <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-xs text-gray-500 mt-0.5">
                      <span className="capitalize">{file.file_category}</span>
                      <span className="hidden sm:inline">•</span>
                      <span>{formatFileSize(file.file_size)}</span>
                      <span className="hidden sm:inline">•</span>
                      <span className="hidden sm:inline">{new Date(file.created_at).toLocaleDateString()}</span>
                      <span className="sm:hidden">{new Date(file.created_at).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row items-center gap-1 flex-shrink-0">
                    {canPreview(file) && (
                      <button
                        onClick={() => handlePreview(file)}
                        className="p-1.5 sm:p-2 text-green-600 hover:bg-green-50 rounded-lg tap-highlight transition-colors"
                        title="プレビュー"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDownload(file)}
                      className="p-1.5 sm:p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg tap-highlight transition-colors"
                      title="ダウンロード"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(file.id)}
                      className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg tap-highlight transition-colors"
                      title="削除"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <>
      {createPortal(modalContent, document.body)}
      <PdfViewer
        url={pdfViewerUrl}
        fileName={pdfViewerFileName}
        isOpen={pdfViewerOpen}
        onClose={() => {
          setPdfViewerOpen(false)
          setPdfViewerUrl('')
          setPdfViewerFileName('')
        }}
        onDownload={handlePdfDownload}
      />
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="ファイルの削除"
        message="このファイルを削除しますか？"
        confirmText="削除"
        cancelText="キャンセル"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setShowDeleteConfirm(false); setPendingDeleteId(null); }}
      />
    </>
  )
}
